import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Table, Badge, Pagination } from 'react-bootstrap';
import { FaEye, FaDownload, FaHeart, FaShoppingCart, FaUser } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import PageHeader from '../components/PageHeader';
import productService from '../services/productService';
import analyticsService from '../services/analyticsService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ProductStats() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [product, setProduct] = useState(null);
  const [stats7d, setStats7d] = useState(null);
  const [statsAll, setStatsAll] = useState(null);
  const [chartViewsData, setChartViewsData] = useState([]);
  const [detailedData, setDetailedData] = useState({
    views: [],
    downloads: [],
    wishlists: [],
    orders: []
  });
  const [pagination, setPagination] = useState({
    views: { page: 1, total: 0, pages: 0 },
    downloads: { page: 1, total: 0, pages: 0 },
    wishlists: { page: 1, total: 0, pages: 0 },
    orders: { page: 1, total: 0, pages: 0 }
  });
  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };

    useEffect(() => {
      fetchProductStats();
      fetchTableData();
    }, [fetchProductStats, fetchTableData]);

  const fetchProductStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch product details
      const productData = await productService.getById(id);
      setProduct(productData);

      // Fetch stats for last 7 days
      const stats7dData = await analyticsService.getProductDetailedStats(id, 7);
      setStats7d(stats7dData);

      // Fetch all time stats
      const statsAllData = await analyticsService.getProductDetailedStats(id, 'all');
      setStatsAll(statsAllData);

      // Fetch views data for chart (last 7 days, all records)
      const chartViews = await analyticsService.getProductViewsWithUsers(id, 1000, 7, 1, 1000);
      setChartViewsData(chartViews || []);

    } catch (err) {
      console.error('Failed to load product stats:', err);
      setError('Failed to load product statistics.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchTableData = useCallback(async () => {
    try {
      // Fetch detailed data with user info (now grouped by backend) with pagination
      const [viewsData, downloadsData, wishlistsData, ordersData] = await Promise.all([
        analyticsService.getProductViewsWithUsers(id, null, null, pagination.views.page, 5),
        analyticsService.getProductDownloadsWithUsers(id, pagination.downloads.page, 5),
        analyticsService.getProductWishlistsWithUsers(id, pagination.wishlists.page, 5),
        analyticsService.getProductOrdersWithUsers(id, pagination.orders.page, 5)
      ]);

      setDetailedData({
        views: viewsData.data || viewsData || [],
        downloads: downloadsData.data || downloadsData || [],
        wishlists: wishlistsData.data || wishlistsData || [],
        orders: ordersData.data || ordersData || []
      });

      // Update pagination info
      setPagination(prev => ({
        views: {
          page: viewsData.page || prev.views.page,
          total: viewsData.total || prev.views.total,
          pages: viewsData.pages || prev.views.pages
        },
        downloads: {
          page: downloadsData.page || prev.downloads.page,
          total: downloadsData.total || prev.downloads.total,
          pages: downloadsData.pages || prev.downloads.pages
        },
        wishlists: {
          page: wishlistsData.page || prev.wishlists.page,
          total: wishlistsData.total || prev.wishlists.total,
          pages: wishlistsData.pages || prev.wishlists.pages
        },
        orders: {
          page: ordersData.page || prev.orders.page,
          total: ordersData.total || prev.orders.total,
          pages: ordersData.pages || prev.orders.pages
        }
      }));

    } catch (err) {
      console.error('Failed to load table data:', err);
    }
  }, [id, pagination.views.page, pagination.downloads.page, pagination.wishlists.page, pagination.orders.page]);

  const handlePageChange = (tableType, newPage) => {
    setPagination(prev => ({
      ...prev,
      [tableType]: { ...prev[tableType], page: newPage }
    }));
  };

  useEffect(() => {
    fetchTableData();
  }, [pagination.views.page, pagination.downloads.page, pagination.wishlists.page, pagination.orders.page, fetchTableData]);

  // Prepare chart data
  const chartData = chartViewsData.length > 0 ? (() => {
    // Initialize last 7 days with 0 counts
    const last7Days = {};
    const today = new Date();
    
    // Initialize last 7 days with 0 counts
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      last7Days[dateKey] = 0;
    }
    
    // Use the chart views data
    chartViewsData.forEach(view => {
      const dateKey = view.date; // Already in YYYY-MM-DD format from backend
      if (last7Days.hasOwnProperty(dateKey)) {
        last7Days[dateKey] = view.count; // Use the count from grouped data
      }
    });
    
    // Create chart data
    const labels = Object.keys(last7Days).map(dateKey => {
      const date = new Date(dateKey);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const data = Object.values(last7Days);
    
    return {
      labels,
      datasets: [
        {
          label: 'Daily Views',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  })() : null;

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  if (loading) {
    return (
      <Container fluid className="px-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title="Product Statistics"
        subtitle={product ? product.title : 'Loading...'}
      />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {product && (
        <>
          {/* Product Info */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={2}>
                      {product.image ? (
                        <img
                          src={getImageUrl(product.image)}
                          alt={product.title}
                          className="rounded"
                          width="80"
                          height="80"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold"
                          style={{ width: '80px', height: '80px', fontSize: '32px' }}
                        >
                          {product.title.charAt(0)}
                        </div>
                      )}
                    </Col>
                    <Col md={10}>
                      <h4 className="mb-1">{product.title}</h4>
                      <p className="text-muted mb-2">{product.short_description}</p>
                      <div className="d-flex gap-3">
                        <span><strong>Price:</strong> ${product.display_price}</span>
                        <span><strong>Category:</strong> {product.category_name}</span>
                        <span><strong>Type:</strong> {product.has_file ? 'Digital' : 'Standard'}</span>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Last 7 Days Stats */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <h5 className="mb-3">Last 7 Days</h5>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Views</p>
                      <h3 className="mb-0">{stats7d?.views || 0}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <FaEye className="text-primary" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Downloads</p>
                      <h3 className="mb-0">{stats7d?.downloads || 0}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <FaDownload className="text-success" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Wishlists</p>
                      <h3 className="mb-0">{stats7d?.wishlists || 0}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      <FaHeart className="text-danger" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Orders</p>
                      <h3 className="mb-0">{stats7d?.orders || 0}</h3>
                    </div>
                    <div className="bg-warning bg-opacity-10 p-3 rounded">
                      <FaShoppingCart className="text-warning" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* All Time Stats */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <h5 className="mb-3">All Time</h5>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Views</p>
                      <h3 className="mb-0">{statsAll?.views || 0}</h3>
                    </div>
                    <div className="bg-primary bg-opacity-10 p-3 rounded">
                      <FaEye className="text-primary" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Downloads</p>
                      <h3 className="mb-0">{statsAll?.downloads || 0}</h3>
                    </div>
                    <div className="bg-success bg-opacity-10 p-3 rounded">
                      <FaDownload className="text-success" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Wishlists</p>
                      <h3 className="mb-0">{statsAll?.wishlists || 0}</h3>
                    </div>
                    <div className="bg-danger bg-opacity-10 p-3 rounded">
                      <FaHeart className="text-danger" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="text-muted mb-1 small">Orders</p>
                      <h3 className="mb-0">{statsAll?.orders || 0}</h3>
                    </div>
                    <div className="bg-warning bg-opacity-10 p-3 rounded">
                      <FaShoppingCart className="text-warning" size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Views Chart */}
          {chartData && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <Card.Body>
                    <h5 className="mb-3">Views Over Time (Last 7 Days)</h5>
                    <div style={{ height: '300px' }}>
                      <Line data={chartData} options={chartOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Detailed Tables */}
          <Row className="g-3 mb-4">
            {/* Views Table */}
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">
                    <FaEye className="me-2 text-primary" />
                    Recent Views ({pagination.views.total} total user-date combinations)
                  </h5>
                  {detailedData.views.length > 0 ? (
                    <>
                      <div className="table-responsive" style={{ height: '250px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th>User</th>
                              <th>Date</th>
                              <th>Views</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.views.map((viewGroup, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FaUser className="me-2 text-muted" />
                                    <div>
                                      <div className="fw-semibold">
                                        {viewGroup.user_name || `User ${viewGroup.user_id || 'Anonymous'}`}
                                      </div>
                                      <small className="text-muted">{viewGroup.user_email || 'N/A'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(viewGroup.date).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>
                                  <Badge bg="primary" pill>{viewGroup.count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {pagination.views.pages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.First
                              disabled={pagination.views.page === 1}
                              onClick={() => handlePageChange('views', 1)}
                            />
                            <Pagination.Prev
                              disabled={pagination.views.page === 1}
                              onClick={() => handlePageChange('views', pagination.views.page - 1)}
                            />
                            {[...Array(Math.min(5, pagination.views.pages))].map((_, i) => {
                              const pageNum = Math.max(1, pagination.views.page - 2) + i;
                              if (pageNum > pagination.views.pages) return null;
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pagination.views.page}
                                  onClick={() => handlePageChange('views', pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            <Pagination.Next
                              disabled={pagination.views.page === pagination.views.pages}
                              onClick={() => handlePageChange('views', pagination.views.page + 1)}
                            />
                            <Pagination.Last
                              disabled={pagination.views.page === pagination.views.pages}
                              onClick={() => handlePageChange('views', pagination.views.pages)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-4" style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <p>No views yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Downloads Table */}
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">
                    <FaDownload className="me-2 text-success" />
                    Downloads ({pagination.downloads.total} total user-date combinations)
                  </h5>
                  {detailedData.downloads.length > 0 ? (
                    <>
                      <div className="table-responsive" style={{ height: '250px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th>User</th>
                              <th>Date</th>
                              <th>Downloads</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.downloads.map((downloadGroup, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FaUser className="me-2 text-muted" />
                                    <div>
                                      <div className="fw-semibold">
                                        {downloadGroup.user_name || `User ${downloadGroup.user_id || 'Anonymous'}`}
                                      </div>
                                      <small className="text-muted">{downloadGroup.user_email || 'N/A'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(downloadGroup.date).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>
                                  <Badge bg="success" pill>{downloadGroup.count}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {pagination.downloads.pages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.First
                              disabled={pagination.downloads.page === 1}
                              onClick={() => handlePageChange('downloads', 1)}
                            />
                            <Pagination.Prev
                              disabled={pagination.downloads.page === 1}
                              onClick={() => handlePageChange('downloads', pagination.downloads.page - 1)}
                            />
                            {[...Array(Math.min(5, pagination.downloads.pages))].map((_, i) => {
                              const pageNum = Math.max(1, pagination.downloads.page - 2) + i;
                              if (pageNum > pagination.downloads.pages) return null;
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pagination.downloads.page}
                                  onClick={() => handlePageChange('downloads', pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            <Pagination.Next
                              disabled={pagination.downloads.page === pagination.downloads.pages}
                              onClick={() => handlePageChange('downloads', pagination.downloads.page + 1)}
                            />
                            <Pagination.Last
                              disabled={pagination.downloads.page === pagination.downloads.pages}
                              onClick={() => handlePageChange('downloads', pagination.downloads.pages)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-4" style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <p>No downloads yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            {/* Wishlists Table */}
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">
                    <FaHeart className="me-2 text-danger" />
                    Wishlists ({pagination.wishlists.total} total users)
                  </h5>
                  {detailedData.wishlists.length > 0 ? (
                    <>
                      <div className="table-responsive" style={{ height: '250px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th>User</th>
                              <th>Date Added</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.wishlists.map((wishlist, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FaUser className="me-2 text-muted" />
                                    <div>
                                      <div className="fw-semibold">
                                        {wishlist.user_name || `User ${wishlist.user_id}`}
                                      </div>
                                      <small className="text-muted">{wishlist.user_email || 'N/A'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(wishlist.created_at).toLocaleDateString()}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {pagination.wishlists.pages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.First
                              disabled={pagination.wishlists.page === 1}
                              onClick={() => handlePageChange('wishlists', 1)}
                            />
                            <Pagination.Prev
                              disabled={pagination.wishlists.page === 1}
                              onClick={() => handlePageChange('wishlists', pagination.wishlists.page - 1)}
                            />
                            {[...Array(Math.min(5, pagination.wishlists.pages))].map((_, i) => {
                              const pageNum = Math.max(1, pagination.wishlists.page - 2) + i;
                              if (pageNum > pagination.wishlists.pages) return null;
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pagination.wishlists.page}
                                  onClick={() => handlePageChange('wishlists', pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            <Pagination.Next
                              disabled={pagination.wishlists.page === pagination.wishlists.pages}
                              onClick={() => handlePageChange('wishlists', pagination.wishlists.page + 1)}
                            />
                            <Pagination.Last
                              disabled={pagination.wishlists.page === pagination.wishlists.pages}
                              onClick={() => handlePageChange('wishlists', pagination.wishlists.pages)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-4" style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <p>No wishlists yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Orders Table */}
            <Col md={6}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">
                    <FaShoppingCart className="me-2 text-warning" />
                    Orders ({pagination.orders.total} total user-date combinations)
                  </h5>
                  {detailedData.orders.length > 0 ? (
                    <>
                      <div className="table-responsive" style={{ height: '250px', overflowY: 'auto' }}>
                        <Table hover className="mb-0">
                          <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr>
                              <th>User</th>
                              <th>Date</th>
                              <th>Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detailedData.orders.map((orderGroup, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <FaUser className="me-2 text-muted" />
                                    <div>
                                      <div className="fw-semibold">
                                        {orderGroup.user_name || `User ${orderGroup.user_id || 'Anonymous'}`}
                                      </div>
                                      <small className="text-muted">{orderGroup.user_email || 'N/A'}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <small className="text-muted">
                                    {new Date(orderGroup.date).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>
                                  <Badge bg="warning" pill>{orderGroup.total_quantity}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {pagination.orders.pages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <Pagination>
                            <Pagination.First
                              disabled={pagination.orders.page === 1}
                              onClick={() => handlePageChange('orders', 1)}
                            />
                            <Pagination.Prev
                              disabled={pagination.orders.page === 1}
                              onClick={() => handlePageChange('orders', pagination.orders.page - 1)}
                            />
                            {[...Array(Math.min(5, pagination.orders.pages))].map((_, i) => {
                              const pageNum = Math.max(1, pagination.orders.page - 2) + i;
                              if (pageNum > pagination.orders.pages) return null;
                              return (
                                <Pagination.Item
                                  key={pageNum}
                                  active={pageNum === pagination.orders.page}
                                  onClick={() => handlePageChange('orders', pageNum)}
                                >
                                  {pageNum}
                                </Pagination.Item>
                              );
                            })}
                            <Pagination.Next
                              disabled={pagination.orders.page === pagination.orders.pages}
                              onClick={() => handlePageChange('orders', pagination.orders.page + 1)}
                            />
                            <Pagination.Last
                              disabled={pagination.orders.page === pagination.orders.pages}
                              onClick={() => handlePageChange('orders', pagination.orders.pages)}
                            />
                          </Pagination>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-4" style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <p>No orders yet</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
}

export default ProductStats;